import { 
    Box, 
    Button, 
    Container, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    Grid, 
    Paper, 
    Slide, 
    TextField, 
    Typography, 
    Card,
    CardContent,
    CardHeader,
    Divider,
    Alert,
    Chip,
    Stack,
    Avatar,
    alpha,
    Fade
} from '@mui/material'
import { useFormik } from 'formik';
import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import { LoadingButton } from '@mui/lab';

import { storeAccessKeys } from '../../../store/slices/AccessKeysSlice';
import CustomDialog from '../../../components/CustomDialog';
import Iconify from '../../../components/Iconify';
import Page from '../../../components/Page';
import ApiUrls from '../../../helper/ApiUrls';
import { API } from '../../../services/axios.service';
import { AccessKeySkeleton } from './Skeletons';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const AccessKey = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [open, setOpen] = useState(false);
    const [getAccessKeyData, setGetAccessKeyData] = useState({})
    const [getLoading, setGetLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const accessOrigin = searchParams.get("access_origin");

    useEffect(() => {
        if (Object?.keys(getAccessKeyData)?.length > 0) {
            accessKeyForm.setValues({
                access_name: getAccessKeyData?.access_details?.api_key,
                aws_access_key_id: getAccessKeyData?.access_details?.aws_access_key_id,
                aws_secret_access_key: getAccessKeyData?.access_details?.aws_secret_access_key,
                aws_default_region: getAccessKeyData?.access_details?.aws_default_region
            })
        }
    }, [open])

    useEffect(() => {
        if (accessOrigin) {
            getAccessKey()
        } else {
            navigate('/dashboard/settings')
        }
    }, [])

    const getAccessKey = () => {
        setGetLoading(true)
        API.get(ApiUrls.GET_POST_ACCESS_KEY)
            .then(res => {
                const accessData = res?.data?.data?.find(item => item.origin === accessOrigin) || {};
                console.log('op details', accessData)
                setGetAccessKeyData(accessData)
                dispatch(storeAccessKeys(res.data?.data ? res.data.data : []));
            }).catch(err => {
                console.log(err)
            })
            .finally(() => {
                setGetLoading(false)
            })
    }

    const deleteAccessKey = (accessKey) => {
        API.delete(ApiUrls.UPDATE_DELETE_ACCESS_KEY(accessKey))
            .then((res) => {
                console.log(res.data)
            })
            .catch(err => {
                console.log(err)
            })
            .finally(() => {
                setShowDeleteConfirm(!showDeleteConfirm)
                getAccessKey()
            })
    }

    const CreateUpdateAccessKey = (values) => {
        const payload = {
            "origin": accessOrigin,
            "access_details": {
                "api_key": values.access_name
            }
        }

        if (accessOrigin === 'bedrock') {
            delete payload.access_details.api_key
            payload.access_details.aws_access_key_id = values.aws_access_key_id
            payload.access_details.aws_secret_access_key = values.aws_secret_access_key
            payload.access_details.aws_default_region = values.aws_default_region
        }

        const response = Object?.keys(getAccessKeyData)?.length > 0 ?
            API.patch(ApiUrls.UPDATE_DELETE_ACCESS_KEY(getAccessKeyData.id), payload) :
            API.post(ApiUrls.GET_POST_ACCESS_KEY, payload);
        response.then((res) => {
            console.log(res.data)
            accessKeyForm.resetForm()
        })
            .catch(err => {
                console.log(err)
            })
            .finally(() => {
                handleOpenCloseModal()
                getAccessKey()
            })
    }

    const handleOpenCloseModal = () => {
        setOpen(!open)
    }

    const openAiValidationSchema = Yup.object().shape({
        access_name: Yup.string().required('Access Key is required'),
    })

    const bedrockValidationSchema = Yup.object().shape({
        aws_access_key_id: Yup.string().required('AWS Access Key Id is required'),
        aws_secret_access_key: Yup.string().required('AWS Secret Access Key is required'),
        aws_default_region: Yup.string().required('AWS Default Region is required'),
    })

    const accessKeyForm = useFormik({
        initialValues: {
            access_name: '',
            aws_access_key_id: '',
            aws_secret_access_key: '',
            aws_default_region: ''
        },
        validationSchema: accessOrigin === 'bedrock' ? bedrockValidationSchema : openAiValidationSchema,
        validateOnBlur: false,
        validateOnChange: true,
        onSubmit: CreateUpdateAccessKey
    })

    // Helper variables
    const isOpenAI = accessOrigin === 'openai';
    const hasAccessKey = Object?.keys(getAccessKeyData)?.length > 0;

    // Helper functions
    const getProviderIcon = () => {
        if (isOpenAI) return 'simple-icons:openai';
        return 'simple-icons:amazonaws';
    };

    const getProviderColor = () => {
        if (isOpenAI) return '#00a67e';
        return '#ff9900';
    };

    const formatSecretKey = (key) => {
        if (!key) return '';
        if (key.length <= 8) return key;
        return `${key.substring(0, 4)}...${key.substring(key.length - 4)}`;
    };

    const getProviderName = () => isOpenAI ? 'OpenAI' : 'AWS Bedrock';

    const getProviderDescription = () => isOpenAI ? 'OpenAI API' : 'AWS Bedrock';

    const getDialogDescription = () => {
        if (isOpenAI) {
            return 'Enter your OpenAI API key to enable AI functionality';
        }
        return 'Enter your AWS credentials to access Bedrock models';
    };

    const getEmptyStateMessage = () => `Add your ${getProviderDescription()} credentials to enable AI functionality`;

    const renderStatusSubheader = () => {
        if (hasAccessKey) {
            return `Your ${getProviderDescription()} credentials are properly configured`;
        }
        return `Add your ${getProviderDescription()} credentials to get started`;
    };

    const renderStatusTitle = () => hasAccessKey ? 'Access Key Configured' : 'No Access Key Found';

    const renderActionButton = () => {
        if (!hasAccessKey) {
            return (
                <Button
                    variant="contained"
                    onClick={handleOpenCloseModal}
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    sx={{
                        background: `linear-gradient(135deg, ${getProviderColor()} 0%, ${alpha(getProviderColor(), 0.8)} 100%)`,
                        '&:hover': {
                            background: `linear-gradient(135deg, ${alpha(getProviderColor(), 0.9)} 0%, ${alpha(getProviderColor(), 0.7)} 100%)`
                        }
                    }}
                >
                    Add Access Key
                </Button>
            );
        }

        return (
            <Button
                variant="outlined"
                color="error"
                onClick={() => setShowDeleteConfirm(!showDeleteConfirm)}
                startIcon={<Iconify icon="eva:trash-2-outline" />}
            >
                Delete
            </Button>
        );
    };

    const renderMainContent = () => {
        if (getLoading) {
            return <AccessKeySkeleton />;
        }

        if (hasAccessKey) {
            return renderAccessKeyDetails();
        }

        return renderEmptyState();
    };

    const renderAccessKeyDetails = () => (
            <Stack spacing={3}>
                {isOpenAI ? renderOpenAIKeyDetails() : renderAWSKeyDetails()}
                {renderUsageDetails()}
            </Stack>
        );

    const renderOpenAIKeyDetails = () => (
            <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    API Key
                </Typography>
                <Paper sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2
                }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                            {formatSecretKey(getAccessKeyData?.access_details?.api_key)}
                        </Typography>
                        <Chip 
                            label="Secured" 
                            size="small" 
                            color="success" 
                            icon={<Iconify icon="eva:shield-fill" />}
                        />
                    </Stack>
                </Paper>
            </Box>
        );

    const renderAWSKeyDetails = () => (
            <Stack spacing={2}>
                <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        AWS Access Key ID
                    </Typography>
                    <Paper sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2
                    }}>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                            {formatSecretKey(getAccessKeyData?.access_details?.aws_access_key_id)}
                        </Typography>
                    </Paper>
                </Box>
                
                <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        AWS Secret Access Key
                    </Typography>
                    <Paper sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2
                    }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                                {formatSecretKey(getAccessKeyData?.access_details?.aws_secret_access_key)}
                            </Typography>
                            <Chip 
                                label="Secured" 
                                size="small" 
                                color="success" 
                                icon={<Iconify icon="eva:shield-fill" />}
                            />
                        </Stack>
                    </Paper>
                </Box>
                
                <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        AWS Region
                    </Typography>
                    <Paper sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2
                    }}>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                            {getAccessKeyData?.access_details?.aws_default_region}
                        </Typography>
                    </Paper>
                </Box>
            </Stack>
        );

    const renderUsageDetails = () => {
        const hasUsageDetails = getAccessKeyData?.usage_details?.message || 
                              getAccessKeyData?.usage_details?.summery || 
                              getAccessKeyData?.usage_details?.generation;

        if (!hasUsageDetails) return null;

        return (
            <Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Usage Information
                </Typography>
                <Stack spacing={2}>
                    {getAccessKeyData?.usage_details?.message && (
                        <Alert severity="info">
                            <Typography variant="body2">
                                <strong>Message:</strong> {getAccessKeyData.usage_details.message}
                            </Typography>
                        </Alert>
                    )}
                    {getAccessKeyData?.usage_details?.summery && (
                        <Alert severity="info">
                            <Typography variant="body2">
                                <strong>Summary:</strong> {getAccessKeyData.usage_details.summery}
                            </Typography>
                        </Alert>
                    )}
                    {getAccessKeyData?.usage_details?.generation && (
                        <Alert severity="info">
                            <Typography variant="body2">
                                <strong>Generation:</strong> {getAccessKeyData.usage_details.generation}
                            </Typography>
                        </Alert>
                    )}
                </Stack>
            </Box>
        );
    };

    const renderEmptyState = () => (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Avatar sx={{ 
                    mx: 'auto', 
                    mb: 2, 
                    bgcolor: 'grey.100',
                    width: 64,
                    height: 64
                }}>
                    <Iconify icon="eva:key-outline" sx={{ color: 'grey.400', fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    No API Access Key Found
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                    {getEmptyStateMessage()}
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleOpenCloseModal}
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    sx={{
                        background: `linear-gradient(135deg, ${getProviderColor()} 0%, ${alpha(getProviderColor(), 0.8)} 100%)`,
                        '&:hover': {
                            background: `linear-gradient(135deg, ${alpha(getProviderColor(), 0.9)} 0%, ${alpha(getProviderColor(), 0.7)} 100%)`
                        }
                    }}
                >
                    Add Access Key
                </Button>
            </Box>
        );

    const renderDialogFields = () => {
        if (isOpenAI) {
            return (
                <TextField
                    autoFocus
                    name="access_name"
                    label="OpenAI API Key"
                    type="password"
                    fullWidth
                    required
                    variant="outlined"
                    placeholder="sk-..."
                    value={accessKeyForm.values.access_name || ''}
                    onChange={accessKeyForm.handleChange}
                    error={!!accessKeyForm.errors.access_name && accessKeyForm.touched.access_name}
                    helperText={accessKeyForm.touched.access_name ? accessKeyForm.errors?.access_name : 'Your API key will be encrypted and stored securely'}
                    InputProps={{
                        startAdornment: <Iconify icon="eva:key-outline" sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                />
            );
        }

        return (
            <>
                <TextField
                    autoFocus
                    name="aws_access_key_id"
                    label="AWS Access Key ID"
                    type="text"
                    fullWidth
                    required
                    variant="outlined"
                    placeholder="AKIA..."
                    value={accessKeyForm.values.aws_access_key_id || ''}
                    onChange={accessKeyForm.handleChange}
                    error={!!accessKeyForm.errors.aws_access_key_id && accessKeyForm.touched.aws_access_key_id}
                    helperText={accessKeyForm.touched.aws_access_key_id ? accessKeyForm.errors?.aws_access_key_id : undefined}
                    InputProps={{
                        startAdornment: <Iconify icon="eva:person-outline" sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                />
                <TextField
                    name="aws_secret_access_key"
                    label="AWS Secret Access Key"
                    type="password"
                    fullWidth
                    required
                    variant="outlined"
                    value={accessKeyForm.values.aws_secret_access_key || ''}
                    onChange={accessKeyForm.handleChange}
                    error={!!accessKeyForm.errors.aws_secret_access_key && accessKeyForm.touched.aws_secret_access_key}
                    helperText={accessKeyForm.touched.aws_secret_access_key ? accessKeyForm.errors?.aws_secret_access_key : undefined}
                    InputProps={{
                        startAdornment: <Iconify icon="eva:key-outline" sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                />
                <TextField
                    name="aws_default_region"
                    label="AWS Region"
                    type="text"
                    fullWidth
                    required
                    variant="outlined"
                    placeholder="us-east-1"
                    value={accessKeyForm.values.aws_default_region || ''}
                    onChange={accessKeyForm.handleChange}
                    error={!!accessKeyForm.errors.aws_default_region && accessKeyForm.touched.aws_default_region}
                    helperText={accessKeyForm.touched.aws_default_region ? accessKeyForm.errors?.aws_default_region : 'Select the AWS region for Bedrock service'}
                    InputProps={{
                        startAdornment: <Iconify icon="eva:globe-outline" sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                />
            </>
        );
    };

    return (
        <Page title={'Access Key Management'}>
            <Container maxWidth="md">
                {/* Header */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 3,
                        background: `linear-gradient(135deg, ${getProviderColor()}15 0%, ${getProviderColor()}05 100%)`,
                        border: `1px solid ${alpha(getProviderColor(), 0.2)}`,
                        borderRadius: 3,
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: alpha(getProviderColor(), 0.1)
                    }} />
                    
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ position: 'relative' }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar sx={{ 
                                bgcolor: getProviderColor(),
                                color: 'white',
                                width: 56,
                                height: 56
                            }}>
                                <Iconify icon={getProviderIcon()} width={28} height={28} />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {getProviderName()} Access Key
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    Manage your {getProviderDescription()} credentials securely
                                </Typography>
                            </Box>
                        </Stack>
                        
                        <Button
                            onClick={() => navigate(`/dashboard/settings`, { state: { backButton: true } })}
                            startIcon={<Iconify icon="eva:arrow-back-outline" />}
                            sx={{ 
                                color: 'text.primary',
                                bgcolor: alpha('#fff', 0.8),
                                '&:hover': { bgcolor: alpha('#fff', 1) }
                            }}
                        >
                            Back to Settings
                        </Button>
                    </Stack>
                </Paper>

                {/* Main Content */}
                <Grid container spacing={3}>
                    {/* Access Key Status Card */}
                    <Grid item size={12}>
                        <Fade in timeout={600}>
                            <Card sx={{ 
                                borderRadius: 3,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                                border: '1px solid',
                                borderColor: 'divider'
                            }}>
                                <CardHeader
                                    avatar={
                                        <Avatar sx={{ 
                                            bgcolor: hasAccessKey ? 'success.main' : 'grey.400',
                                            color: 'white'
                                        }}>
                                            <Iconify icon={hasAccessKey ? "eva:checkmark-circle-2-fill" : "eva:info-outline"} />
                                        </Avatar>
                                    }
                                    title={
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {renderStatusTitle()}
                                        </Typography>
                                    }
                                    subheader={
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {renderStatusSubheader()}
                                        </Typography>
                                    }
                                    action={
                                        <Stack direction="row" spacing={1}>
                                            {renderActionButton()}
                                        </Stack>
                                    }
                                />
                                
                                <Divider />
                                
                                <CardContent>
                                    {renderMainContent()}
                                </CardContent>
                            </Card>
                        </Fade>
                    </Grid>
                </Grid>
            </Container>

            {/* Delete Confirmation Dialog */}
            <CustomDialog
                open={showDeleteConfirm}
                title={"Delete Access Key"}
                contentText="Are you sure you want to remove this access key? This action cannot be undone and will disable AI functionality."
                onConfirm={() => { deleteAccessKey(getAccessKeyData?.id) }}
                onClose={() => { setShowDeleteConfirm(false) }}
            />

            {/* Add/Edit Dialog */}
            <Dialog 
                fullWidth 
                maxWidth="sm" 
                open={open} 
                onClose={handleOpenCloseModal} 
                TransitionComponent={Transition}
                PaperProps={{
                    sx: { borderRadius: 3 }
                }}
            >
                <DialogTitle sx={{ pb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ 
                            bgcolor: getProviderColor(),
                            color: 'white'
                        }}>
                            <Iconify icon={getProviderIcon()} />
                        </Avatar>
                        <Box>
                            <Typography variant="h6">
                                {hasAccessKey ? 'Update' : 'Add'} {getProviderName()} Access Key
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {getDialogDescription()}
                            </Typography>
                        </Box>
                    </Stack>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ pt: 3 }}>
                    <Stack spacing={3}>
                        {renderDialogFields()}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 3, pt: 2 }}>
                    <Button 
                        disabled={accessKeyForm.isSubmitting} 
                        variant="outlined" 
                        onClick={() => {
                            handleOpenCloseModal()
                            accessKeyForm.resetForm()
                        }}
                        size="large"
                    >
                        Cancel
                    </Button>
                    <LoadingButton 
                        variant="contained" 
                        disabled={!accessKeyForm.isValid} 
                        loading={accessKeyForm.isSubmitting} 
                        onClick={accessKeyForm.handleSubmit}
                        size="large"
                        sx={{
                            background: `linear-gradient(135deg, ${getProviderColor()} 0%, ${alpha(getProviderColor(), 0.8)} 100%)`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${alpha(getProviderColor(), 0.9)} 0%, ${alpha(getProviderColor(), 0.7)} 100%)`
                            }
                        }}
                    >
                        {hasAccessKey ? 'Update' : 'Save'} Access Key
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Page>
    )
}

export default AccessKey